import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface FloatingSearchBarProps {
    onSearch: (query: string) => void;
    resultsCount: number;
    isSearching: boolean;
}

export const FloatingSearchBar = ({ onSearch, resultsCount, isSearching }: FloatingSearchBarProps) => {
    const [query, setQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check for Ctrl+Q (Windows/Linux) or Cmd+Q (Mac)
            if ((event.ctrlKey || event.metaKey) && event.key === 'q') {
                event.preventDefault();
                inputRef.current?.focus();
            }

            // Close search on Escape
            if (event.key === 'Escape' && isFocused) {
                inputRef.current?.blur();
                setQuery('');
                onSearch('');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFocused, onSearch]);

    const handleQueryChange = (value: string) => {
        setQuery(value);
        onSearch(value);
    };

    const handleClear = () => {
        setQuery('');
        onSearch('');
        inputRef.current?.focus();
    };

    const handleFocus = () => {
        setIsFocused(true);
        // Select all text when focused
        inputRef.current?.select();
    };

    return (
        <div className="fixed bottom-6 right-6 z-40">
            <div
                className={`
                    flex items-center gap-2 bg-white border-2 border-gray-300 rounded-full shadow-lg
                    transition-all duration-200 ease-in-out
                    ${isFocused ? 'w-80' : 'w-64'}
                `}
            >
                <div className="pl-4 flex items-center">
                    <Search className="w-5 h-5 text-gray-400" />
                </div>

                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => handleQueryChange(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={() => setIsFocused(false)}
                    placeholder="Search templates"
                    className="flex-1 py-3 outline-none text-sm bg-transparent"
                />

                {query && (
                    <div className="flex items-center gap-2 pr-2">
                        {isSearching && (
                            <span className="text-xs text-gray-500 font-medium">
                                {resultsCount} {resultsCount === 1 ? 'result' : 'results'}
                            </span>
                        )}
                        <button
                            onClick={handleClear}
                            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                            title="Clear search"
                        >
                            <X className="w-4 h-4 text-gray-400" />
                        </button>
                    </div>
                )}

                {!query && (
                    <div className="pr-4 text-xs text-gray-400 font-medium">
                        Ctrl+Q
                    </div>
                )}
            </div>
        </div>
    );
};
